export const getHTMLElement = (selector: string): HTMLElement | null => {
  const element = document.querySelector(selector) as HTMLElement;
  if (!element) {
    console.warn('Could not find element with selector: ' + selector);
  }
  return element;
};

export const getHTMLElements = (selector: string): HTMLElement[] => {
  const elements = document.querySelectorAll(
    selector,
  ) as NodeListOf<HTMLElement>;
  if (!elements) {
    console.warn('Could not find elements with selector: ' + selector);
  }
  return Array.from(elements);
};

export const toggleDisableClass = (
  element: HTMLElement | null,
  disable: boolean,
) => {
  if (element === null) {
    return;
  }

  if (disable) {
    element.classList.add('disabled');
  } else {
    element.classList.remove('disabled');
  }
};

export const setElementDisabledState = (
  element: HTMLElement | null,
  disabled: boolean,
): void => {
  if (disabled) {
    element?.setAttribute('disabled', '');
  } else {
    element?.removeAttribute('disabled');
  }
};

export const toggleFakeLinkDisableClass = (
  element: HTMLElement | null,
  disable: boolean,
) => {
  if (element === null) {
    return;
  }

  if (disable) {
    element.classList.add('fake-link-disabled');
  } else {
    element.classList.remove('fake-link-disabled');
  }
};

export const calculatePageOffsetY = (element: HTMLElement): number => {
  let offsetY = 0;
  let el: HTMLElement | null = element;
  while (el !== null) {
    offsetY += el.offsetTop;
    el = el.offsetParent as HTMLElement;
  }
  return offsetY;
};

export const parseHTMLString = (htmlStr: string) => {
  const parser = new DOMParser();
  return parser.parseFromString(htmlStr, 'text/html');
};

export const toDocumentFragment = (doc: Document) => {
  const fragment = doc.createDocumentFragment();
  const bodyChildNodes = doc.body.childNodes;

  while (bodyChildNodes.length > 0) {
    fragment.appendChild(bodyChildNodes[0]);
  }

  return fragment;
};

export const removeTablesWidth = (doc: Document) => {
  const elements = doc.querySelectorAll<HTMLElement>('table, th, td');

  elements.forEach((element) => {
    element.style.removeProperty('width');
  });
};

const getPxWidth = (element: HTMLElement) => {
  const match = /^(\d+(?:\.\d+)?)px$/.exec(element.style.width);
  return match ? parseFloat(match[1]) : undefined;
};

const sum = (values: number[]) =>
  values.reduce((total, value) => total + value, 0);

const toPercentage = (width: number, containerWidth: number) =>
  `${Number(Math.min((width / containerWidth) * 100, 100).toPrecision(4))}%`;

const isCoveredByRowSpan = (rows: HTMLTableRowElement[], index: number) =>
  rows
    .slice(0, index)
    .some((row, rowIndex) =>
      Array.from(row.cells).some((cell) => rowIndex + cell.rowSpan > index),
    );

const getColumnPxWidths = (table: HTMLTableElement) => {
  const rows = Array.from(table.rows);
  const row = rows.find(
    ({ cells }, index) =>
      cells.length > 0 &&
      Array.from(cells).every(
        (cell) => cell.colSpan === 1 && getPxWidth(cell),
      ) &&
      !isCoveredByRowSpan(rows, index),
  );
  return row && (Array.from(row.cells, getPxWidth) as number[]);
};

const replaceColgroup = (table: HTMLTableElement, columnWidths: number[]) => {
  table
    .querySelectorAll(':scope > colgroup')
    .forEach((colgroup) => colgroup.remove());
  const tableWidth = sum(columnWidths);
  const colgroup = table.ownerDocument.createElement('colgroup');
  columnWidths.forEach((width) => {
    const col = table.ownerDocument.createElement('col');
    col.style.width = toPercentage(width, tableWidth);
    colgroup.appendChild(col);
  });
  table.insertBefore(
    colgroup,
    table.caption ? table.caption.nextSibling : table.firstChild,
  );
};

const scaleTable = (figure: HTMLElement, textWidth: number) => {
  const table = figure.querySelector('table');
  if (!table) {
    return;
  }
  const columnWidths = getColumnPxWidths(table);
  const tableWidth =
    getPxWidth(figure) ?? (columnWidths ? sum(columnWidths) : undefined);
  const cell = figure.parentElement?.closest<HTMLElement>('td, th');
  const containerWidth = cell ? getPxWidth(cell) : textWidth;
  if (tableWidth && containerWidth) {
    figure.style.width = toPercentage(tableWidth, containerWidth);
  }
  if (columnWidths) {
    replaceColgroup(table, columnWidths);
  }
};

export const removeTablePxWidths = (doc: Document) => {
  doc
    .querySelectorAll<HTMLElement>('figure.table, table, col, th, td')
    .forEach((element) => {
      if (getPxWidth(element) !== undefined) {
        element.style.removeProperty('width');
      }
    });
};

export const fitTablesToTextWidth = (doc: Document, textWidth: number) => {
  doc
    .querySelectorAll<HTMLElement>('figure.table')
    .forEach((figure) => scaleTable(figure, textWidth));
  removeTablePxWidths(doc);
};

export const removeNegativeMarginLeft = (doc: Document) => {
  const elements = doc.querySelectorAll<HTMLElement>(
    'p, h1, h2, h3, h4, h5, h6, ol, ul',
  );

  elements.forEach((element) => {
    if (element.style.marginLeft.startsWith('-')) {
      element.style.removeProperty('margin-left');
    }
  });
};

export const removeStyleTags = (doc: Document) => {
  const styles = doc.querySelectorAll('style');

  styles.forEach((style) => {
    style.remove();
  });
};

export const extractAttachmentIdFromHref = (
  href: string,
): string | undefined => {
  const regex = /\/attachments\/(\d+)/;
  let m;
  let attachmentId;

  if ((m = regex.exec(href)) !== null) {
    // access the capture group
    attachmentId = m[1];
  }

  return attachmentId;
};

export const getBookmarkTarget = (href: string): Element | undefined => {
  if (!href.includes('#')) {
    return undefined;
  }

  const targetElement = document.getElementById(href.split('#')[1]);
  if (!targetElement) {
    return undefined;
  }

  return targetElement;
};

export const scrollToBookmarkTarget = (el: Element): void => {
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
